import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    Container,
    Icon,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';
import { getUserPreferredFullName } from 'utils/helper';

const Desktop = ({
    currentAssignment,
    studentSubmissionsGroupedByAccountUser,
    filters,
    setFilters,
    checked,
    setChecked,
    columns,
    sortState,
    handleSetSortState,
    getFilterCount,
    onClickBack,
    onClickView,
    resetSubmissions,
    resetCheckedSubmissions,
    exportToCSV,
}) => {
    const { t } = useTranslation('assignment');

    return (
        <Container sx={{ height: '100%' }} device={'desktop'}>
            <RootStack spacing={5}>
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                    {currentAssignment.title}
                </Text>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                    <Stack alignItems={'center'} direction={'row'} spacing={4}>
                        <Button
                            sx={{ flexShrink: 0 }}
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'secondary'}
                            onClick={() => setFilters((current) => ({ ...current, open: true }))}
                        >
                            {t('common:controls.sort-filter', 'Sort & Filter')} ({getFilterCount(filters, sortState)})
                        </Button>
                        <TextInput
                            sx={{ width: '225px' }}
                            endIcon={<Icon kind={'search'} />}
                            placeholder={t('common:controls.search', 'Search')}
                            value={filters.search}
                            onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                        />
                    </Stack>
                    <Stack alignItems={'center'} direction={'row'} spacing={4}>
                        {checked.length > 0 ? (
                            <>
                                <Button color={'neutral'} variant={'tertiary'} onClick={() => setChecked([])}>
                                    {t('common:controls.cancel', 'Cancel')}
                                </Button>
                                <Button
                                    color={'destructive'}
                                    startIcon={<Icon kind={'refresh'} />}
                                    variant={'secondary'}
                                    onClick={resetCheckedSubmissions}
                                >
                                    {t('common:controls.reset-count', 'Reset', { count: checked.length })}
                                </Button>
                            </>
                        ) : (
                            <Button
                                color={'destructive'}
                                startIcon={<Icon kind={'refresh'} />}
                                variant={'secondary'}
                                onClick={resetSubmissions}
                            >
                                {t('common:controls.reset-drafts', 'Reset Drafts')}
                            </Button>
                        )}
                        <Button
                            startIcon={<Icon kind={'download'} />}
                            variant={'secondary'}
                            tooltip={{
                                component: t('export-feedback-csv', 'Export feedback to a CSV'),
                            }}
                            onClick={exportToCSV}
                        >
                            {t('common:controls.export-to-csv', 'Export to CSV')}
                        </Button>
                    </Stack>
                </Stack>
                <CustomScrollableTable aria-label={'student drafts'} size={'small'}>
                    <ScrollableTableHead>
                        <ScrollableTableRow>
                            <ScrollableTableCell isFixed>
                                <Checkbox
                                    checked={
                                        studentSubmissionsGroupedByAccountUser.length > 0 &&
                                        studentSubmissionsGroupedByAccountUser.every(({ owner_account_id }) =>
                                            checked.includes(owner_account_id)
                                        )
                                    }
                                    onChange={() => {
                                        setChecked(() => {
                                            return studentSubmissionsGroupedByAccountUser.every((s) =>
                                                checked.includes(s.owner_account_id)
                                            )
                                                ? []
                                                : studentSubmissionsGroupedByAccountUser.map(
                                                      ({ owner_account_id }) => owner_account_id
                                                  );
                                        });
                                    }}
                                />
                            </ScrollableTableCell>
                            <ScrollableTableSortableHeaderCells
                                columns={columns}
                                sortState={sortState}
                                handleSetSortState={handleSetSortState}
                            />
                            <ScrollableTableCell>
                                {t('formative-feedback.draft.title', 'Formative Draft')}
                            </ScrollableTableCell>
                        </ScrollableTableRow>
                    </ScrollableTableHead>
                    <ScrollableTableBody>
                        {studentSubmissionsGroupedByAccountUser.map((accountUser) => (
                            <ScrollableTableRow key={accountUser.owner_account_id}>
                                <ScrollableTableCell isFixed>
                                    <Checkbox
                                        checked={checked.includes(accountUser.owner_account_id)}
                                        onChange={() => {
                                            setChecked((current) => {
                                                if (current.includes(accountUser.owner_account_id)) {
                                                    return current.filter((id) => id !== accountUser.owner_account_id);
                                                }
                                                return [...current, accountUser.owner_account_id];
                                            });
                                        }}
                                    />
                                </ScrollableTableCell>
                                <ScrollableTableCell>{getUserPreferredFullName(accountUser)}</ScrollableTableCell>
                                <ScrollableTableCell>{accountUser.submissions.length}</ScrollableTableCell>
                                <ScrollableTableCell>
                                    <Button
                                        disabled={!accountUser.submissions.length}
                                        variant={'borderless'}
                                        onClick={() => onClickView(accountUser)}
                                    >
                                        {t('common:controls.view', 'View')}
                                    </Button>
                                </ScrollableTableCell>
                            </ScrollableTableRow>
                        ))}
                    </ScrollableTableBody>
                </CustomScrollableTable>
            </RootStack>
        </Container>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
        minHeight: '100%',
    };
});

const CustomScrollableTable = styled(ScrollableTable)(() => {
    return {
        flex: '1',
        '& .MuiTableCell-root': {
            '&:nth-child(1)': {
                left: 0,
                minWidth: '74px',
                maxWidth: '74px',
                width: '74px',
            },
        },
    };
});

export default Desktop;
